import type { ReactNode, RefObject } from 'react';
import { memo } from 'react';

import type { HeaderGroup, Row, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { Box, ButtonBase, useTheme } from '@/shared/ui';
import { ArrowDownward, ArrowUpward, InfoOutlined, UnfoldMore } from '@/shared/ui/icons';

import { Z_INDEX } from '@/config/z-index';
import type { CatalogItem } from '@/entities/catalog-item';
import Tooltip from '@/shared/ui/primitives/Tooltip';
import CatalogTableRow from '../CatalogTableRow/CatalogTableRow';

interface CatalogTableGridProps {
  table: Table<CatalogItem>;
  rows: Row<CatalogItem>[];
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  isForwardCurves: boolean;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}

const CatalogTableGrid = ({
  table,
  rows,
  rowVirtualizer,
  tableContainerRef,
  isForwardCurves,
  isInDashboard,
  renderFactsheetButton
}: CatalogTableGridProps) => {
  const { palette } = useTheme();

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        position: 'relative',
        zIndex: Z_INDEX.sticky
      }}
    >
      <Box sx={{ display: 'grid' }}>
        <Box
          sx={{
            display: 'grid',
            position: 'sticky',
            top: 0,
            zIndex: Z_INDEX.raised,
            background: palette.gx.tileDark
          }}
        >
          {table.getHeaderGroups().map((headerGroup: HeaderGroup<CatalogItem>) => (
            <Box
              key={headerGroup.id}
              sx={{ display: 'flex', padding: '8px', paddingLeft: '16px' }}
            >
              {headerGroup.headers.map(header => (
                <Box
                  key={header.id}
                  sx={{
                    display: 'flex',
                    width: header.getSize(),
                    paddingBottom: '0',
                    paddingRight: '8px',
                    justifyContent: ['Last', 'Change'].includes(
                      header.column.columnDef.header as string
                    )
                      ? 'flex-end'
                      : 'flex-start'
                  }}
                >
                  <ButtonBase
                    disableRipple
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      border: 'none',
                      color: 'inherit',
                      background: 'transparent',
                      fontFamily: 'inherit',
                      padding: 0,
                      margin: 0,
                      fontWeight: 700,
                      fontSize: '16px'
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.column.columnDef.header !== 'AdditionalItem' &&
                      flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.columnDef.header === 'Info' && (
                      <Tooltip title='Index information pages provide details of the methodology behind the index.'>
                        <InfoOutlined sx={{ ml: 0.5, fontSize: 12 }} />
                      </Tooltip>
                    )}
                    {(header.column.getCanSort() &&
                      header.column.columnDef.header !== 'Info' &&
                      header.column.columnDef.header !== 'AdditionalItem' &&
                      header.column.id !== 'Symbol' &&
                      {
                        asc: (
                          <ArrowUpward
                            fontSize='small'
                            sx={{ ml: 0.5 }}
                          />
                        ),
                        desc: (
                          <ArrowDownward
                            fontSize='small'
                            sx={{ ml: 0.5 }}
                          />
                        )
                      }[header.column.getIsSorted() as string]) ?? (
                      <UnfoldMore
                        fontSize='small'
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </ButtonBase>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        <Box sx={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map(virtualRow => {
            const row = rows[virtualRow.index];
            return (
              <CatalogTableRow
                key={virtualRow.key}
                isForwardCurves={isForwardCurves}
                isInDashboard={isInDashboard}
                renderFactsheetButton={renderFactsheetButton}
                row={row}
                virtualRow={virtualRow}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CatalogTableGrid;
